import { Box, MantineProvider } from "@mantine/core";
import { useModals } from "@mantine/modals";
import { showOldNotification as showNotification } from "@rpi/openapi-core";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import { Flex, RpiButton } from "@rpi/openapi-core";
import React, { useState } from "react";
import { useOutlet, useParams } from "react-router-dom";
import { AppPage } from "../../components/AppPage";
import { CallbacksList } from "../../components/CallbacksList";
import { JsonBox } from "../../components/JsonBox";
import { RpiTableV2Container } from "../../components/RpiTableV2Container";
import { RpiSingleTable } from "../../components/tables/RpiSingleTable";
import { orderEventColumns } from "../../configuration/columns/orderEvent.column";
import { orderItemColumns } from "../../configuration/columns/orderItem.column";
import { orderRefundColumns } from "../../configuration/columns/payment.column";
import { CustomShipmentResponse, shipmentColumns } from "../../configuration/columns/shipment.column";
import { orderDestinationAddressRows, orderDestinationRows, orderRows } from "../../configuration/rows/order.row";
import { featureFlags } from "../../featureFlags";
import { useAuthUser } from "../../hooks/useAuthUser";
import { getRoutePath } from "../../hooks/useNavigateRoute";
import { RpiTable } from "../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../theme/rpaTheme";
import { formatCurrency, safelyParseJson } from "../../utils/misc.util";
import { OrderRefundView } from "./OrderRefundView";
import { ReprintForm } from "./ReprintForm";

const CANCELLABLE_STATUSES: GraphQL.OrderStatus[] = [
  GraphQL.OrderStatus.Created, // -> CANCELLED (no cost)
  GraphQL.OrderStatus.PricingCompleted, // -> CANCELLED (no cost)
  GraphQL.OrderStatus.Received, // -> CANCELLED (no cost)
  GraphQL.OrderStatus.WaitingInHoldingBin, // -> CANCELLED (no cost)
  GraphQL.OrderStatus.WaitingForSubmission, // -> CANCELLED_BEFORE_SUBMISSION (no cost probably)
  GraphQL.OrderStatus.Submitting, // -> CANCELLED_AFTER_SUBMISSION (costs may occur)
  GraphQL.OrderStatus.Submitted, // -> CANCELLED_AFTER_SUBMISSION (costs may occur)
];

const NON_UPDATABLE_STATUSES: GraphQL.OrderStatus[] = [
  GraphQL.OrderStatus.Received,
  GraphQL.OrderStatus.Created,
  GraphQL.OrderStatus.AssetsProcessed,
  GraphQL.OrderStatus.WaitingForTaxes,
  GraphQL.OrderStatus.PricingCompleted,
  GraphQL.OrderStatus.WaitingForSubmission,
  GraphQL.OrderStatus.Submitting,
  GraphQL.OrderStatus.CancelledBeforeSubmission,
  GraphQL.OrderStatus.WaitingInHoldingBin,
  GraphQL.OrderStatus.Failed,
];

type Order = GraphQLQueryData<GraphQL.OrderQuery>;

function getOrderIdentifier(order: Order) {
  if (!order.customer?.publicId || !order.customerOrderId) return null;
  return { customerPublicId: order.customer.publicId, customerOrderId: order.customerOrderId };
}

function canCancelOrder(order: Order): boolean {
  return order.status ? CANCELLABLE_STATUSES.includes(order.status) : false;
}

function canRefundOrder(order: Order): boolean {
  return order.paymentStatus === GraphQL.PaymentStatus.Paid && (order.remainingPossibleRefund as number) > 0;
}

function canReleaseFromHoldingBin(order: Order): boolean {
  return (
    order.publicOrderStatus === GraphQL.PublicOrderStatus.ValidHoldingBin &&
    (order.paymentStatus === GraphQL.PaymentStatus.Paid || order.paymentStatus === GraphQL.PaymentStatus.ExternallyPaid)
  );
}

function canRequestPrinterUpdate(order: Order): boolean {
  return order.status ? !NON_UPDATABLE_STATUSES.includes(order.status) : false;
}

interface ConfirmationAction {
  title: string;
  confirmMessage: React.ReactNode;
  onConfirm: () => Promise<unknown>;
  successMessage: string;
  errorMessage: string;
}

function openConfirmationModal(
  modals: ReturnType<typeof useModals>,
  order: Order,
  action: ConfirmationAction,
  onRefetch: () => void
) {
  return modals.openContextModal("confirmation", {
    title: action.title,
    innerProps: {
      message: action.confirmMessage,
      onConfirm: async () => {
        return action
          .onConfirm()
          .then(() => {
            showNotification({ title: action.successMessage, message: order.customerOrderId, color: "green" });
            onRefetch();
          })
          .catch((error) => {
            showNotification({ title: action.errorMessage, message: error.message, color: "red" });
          });
      },
    },
  });
}

export const OrderView: React.FC = () => {
  const { customerPublicId, customerOrderId } = useParams();

  const user = useAuthUser();
  const outlet = useOutlet();

  const { data, isLoading, error, refetch } = GraphQL.useOrderQuery(
    {
      orderIdentifier: {
        customerPublicId: customerPublicId ?? "",
        customerOrderId: customerOrderId ?? "",
      },
      isSandbox: user.isSandbox,
    },
    { enabled: !!customerPublicId && !!customerOrderId }
  );
  const order = data?.orderByIdentifier;

  const customOrderShipments = React.useMemo(() => {
    if (!order || !order.shipments) return;

    return order.shipments.map((shipment) => {
      const parsed =
        typeof shipment.rawShipmentData === "string"
          ? safelyParseJson(shipment.rawShipmentData)
          : shipment.rawShipmentData;

      return {
        ...shipment,
        parsedRawShipmentData: data === null || typeof data !== "object" ? undefined : parsed,
      };
    }) as CustomShipmentResponse<NonNullable<GraphQLQueryData<GraphQL.OrderQuery>["shipments"]>[number]>[];
  }, [order?.shipments]);

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  return (
    <AppPage path={[{ name: "Orders", to: getRoutePath("orders") }, customerOrderId]} onReload={refetch}>
      {outlet}

      <AppPage.Section title="Actions" id="actions" isLoading={isLoading} error={error}>
        {order && <OrderActions order={order} onRefetch={refetch} />}
      </AppPage.Section>

      <AppPage.Section mt={32} title="Data" id="data">
        <RpiSingleTable isLoading={isLoading} error={error} data={order} rows={orderRows} />
      </AppPage.Section>

      <AppPage.Section mt={32} title="Destination" id="destination">
        <RpiSingleTable isLoading={isLoading} error={error} data={order?.destination} rows={orderDestinationRows} />
      </AppPage.Section>

      <AppPage.Section mt={24} title="Address" size="small" id="destination-address">
        <RpiSingleTable
          isLoading={isLoading}
          error={error}
          data={order?.destination?.address}
          rows={orderDestinationAddressRows}
        />
      </AppPage.Section>

      <AppPage.Section mt={32} title="Shipments" id="shipments">
        {featureFlags.newTableView ? (
          <MantineProvider theme={rpaTheme}>
            <RpiTableV2Container>
              <RpiTable
                variant="normalV2"
                emptyMessage="Shipments will appear here."
                isLoading={isLoading}
                error={error}
                perPage={customOrderShipments?.length}
                data={customOrderShipments}
                columns={shipmentColumns}
                getRowId={user.is.SUPER_ADMIN ? (row) => String(row.id) : undefined}
                withSubRow={
                  user.is.SUPER_ADMIN
                    ? {
                        hasSubRow: ({ rawShipmentData }) => !!rawShipmentData,
                        renderSubRow: (row) => {
                          if (!row.parsedRawShipmentData) return String(row.rawShipmentData);
                          return <JsonBox sx={{ background: "inherit" }} data={row.parsedRawShipmentData} />;
                        },
                      }
                    : undefined
                }
              />
            </RpiTableV2Container>
          </MantineProvider>
        ) : (
          <RpiTable
            emptyMessage="Shipments will appear here."
            isLoading={isLoading}
            error={error}
            perPage={3}
            data={customOrderShipments}
            columns={shipmentColumns}
            getRowId={user.is.SUPER_ADMIN ? (row) => String(row.id) : undefined}
            withSubRow={
              user.is.SUPER_ADMIN
                ? {
                    hasSubRow: ({ rawShipmentData }) => !!rawShipmentData,
                    renderSubRow: (row) => {
                      if (!row.parsedRawShipmentData) return String(row.rawShipmentData);
                      return <JsonBox sx={{ background: "inherit" }} data={row.parsedRawShipmentData} />;
                    },
                  }
                : undefined
            }
          />
        )}
      </AppPage.Section>

      {order?.pricing && (
        <AppPage.Section mt={32} title="Pricing" id="pricing">
          <Flex.Column
            px={16}
            py={8}
            sx={(theme) => ({
              borderRadius: 6,
              background: theme.colors.sky[5],
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: theme.colors.calm[5],
              padding: 8,
            })}
          >
            <Box
              component="table"
              sx={(theme) => ({
                tableLayout: "fixed",
                borderCollapse: "collapse",
                "& td": {
                  padding: "8px 0px",
                  "&:last-of-type": {
                    textAlign: "right",
                  },
                },
                "& > tbody > tr": {
                  borderBottom: `1px dashed ${theme.colors.calm[5]}`,
                  "&:last-of-type": {
                    borderBottom: `1px solid ${theme.colors.calm[5]}`,
                  },
                  "& > td": {
                    ...theme.other.fontTypes.p2,
                    fontWeight: theme.other.fontWeights.regular,

                    "&:first-of-type": {
                      color: theme.colors.brand[5],
                    },
                    "&:last-of-type": {
                      color: theme.colors.text[5],
                    },
                  },
                },
                "& > tfoot": {
                  "& > tr > td": {
                    ...theme.other.fontTypes.p2,
                    fontWeight: theme.other.fontWeights.bold,

                    "&:first-of-type": {
                      color: theme.colors.brand[5],
                    },
                    "&:last-of-type": {
                      color: theme.colors.text[5],
                    },
                  },
                },
              })}
            >
              <tbody>
                <tr>
                  <td>Items Total:</td>
                  <td>{formatCurrency(order.currency, order.pricing?.itemsTotal || 0)}</td>
                </tr>
                <tr>
                  <td>Shipping Total:</td>
                  <td>{formatCurrency(order.currency, order.pricing?.shippingTotal || 0)}</td>
                </tr>
                <tr>
                  <td>Taxes Total:</td>
                  <td>{formatCurrency(order.currency, order.pricing?.taxesTotal || 0)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Order Total:</td>
                  <td>{formatCurrency(order.currency, order.pricing?.orderTotal || 0)}</td>
                </tr>
              </tfoot>
            </Box>
          </Flex.Column>
        </AppPage.Section>
      )}

      {order?.refunds && (
        <AppPage.Section mt={32} title="Refunds" id="refunds">
          {featureFlags.newTableView ? (
            <MantineProvider theme={rpaTheme}>
              <RpiTableV2Container>
                <RpiTable
                  variant="normalV2"
                  emptyMessage="Order refunds will appear here."
                  isLoading={isLoading}
                  error={error}
                  perPage={order?.refunds?.length}
                  data={order?.refunds}
                  columns={orderRefundColumns}
                />
              </RpiTableV2Container>
            </MantineProvider>
          ) : (
            <RpiTable
              emptyMessage="Order refunds will appear here."
              isLoading={isLoading}
              error={error}
              perPage={3}
              data={order?.refunds}
              columns={orderRefundColumns}
            />
          )}
        </AppPage.Section>
      )}

      <AppPage.Section mt={32} title="Items" id="items">
        {featureFlags.newTableView ? (
          <MantineProvider theme={rpaTheme}>
            <RpiTableV2Container>
              <RpiTable
                variant="normalV2"
                emptyMessage="No order items."
                isLoading={isLoading}
                error={error}
                perPage={order?.orderItems?.length}
                data={order?.orderItems}
                columns={orderItemColumns(customerPublicId ?? "", customerOrderId ?? "")}
              />
            </RpiTableV2Container>
          </MantineProvider>
        ) : (
          <RpiTable
            emptyMessage="No order items."
            isLoading={isLoading}
            error={error}
            perPage={3}
            data={order?.orderItems}
            columns={orderItemColumns(customerPublicId ?? "", customerOrderId ?? "")}
          />
        )}
      </AppPage.Section>

      <AppPage.Section mt={32} title="Events" id="events">
        {featureFlags.newTableView ? (
          <MantineProvider theme={rpaTheme}>
            <RpiTableV2Container>
              <RpiTable
                variant="normalV2"
                emptyMessage="Order events will appear here."
                isLoading={isLoading}
                error={error}
                perPage={order?.orderEvents?.length}
                data={order?.orderEvents}
                columns={orderEventColumns}
                getRowId={(row) => String(row.id)}
                withSubRow={{
                  hasSubRow: ({ data }) => !!data,
                  renderSubRow: (row) => {
                    const data = typeof row.data === "string" ? safelyParseJson(row.data) : row.data;
                    if (data === null || typeof data !== "object") return String(row.data);
                    return <JsonBox sx={{ background: "inherit" }} data={data} />;
                  },
                }}
              />
            </RpiTableV2Container>
          </MantineProvider>
        ) : (
          <RpiTable
            emptyMessage="Order events will appear here."
            isLoading={isLoading}
            error={error}
            perPage={3}
            data={order?.orderEvents}
            columns={orderEventColumns}
            getRowId={(row) => String(row.id)}
            withSubRow={{
              hasSubRow: ({ data }) => !!data,
              renderSubRow: (row) => {
                const data = typeof row.data === "string" ? safelyParseJson(row.data) : row.data;
                if (data === null || typeof data !== "object") return String(row.data);
                return <JsonBox sx={{ background: "inherit" }} data={data} />;
              },
            }}
          />
        )}
      </AppPage.Section>

      {order?.callbacks && order?.callbacks.length > 0 && (
        <AppPage.Section mt={32} title="Callbacks" id="callbacks2">
          <CallbacksList callbacks={order.callbacks} webhookUrl={order.webhookUri} />
        </AppPage.Section>
      )}
    </AppPage>
  );
};

interface OrderActionsProps {
  order: GraphQLQueryData<GraphQL.OrderQuery>;
  onRefetch: () => void;
}

const OrderActions: React.FC<OrderActionsProps> = ({ order, onRefetch }) => {
  const user = useAuthUser();
  const [opened, setOpened] = useState(false);
  const modals = useModals();

  const { mutateAsync: cancelOrderMutate, ...cancelOrderMutation } = GraphQL.useAdminCancelOrderMutation();
  const { mutateAsync: updateOrderStatus, ...updateOrderStatusMutation } = GraphQL.useAdminUpdateOrderStatusMutation();
  const { mutateAsync: releaseFromHoldingBinMutate, ...releaseFromHoldingBinMutation } =
    GraphQL.useAdminReleaseFromHoldingBinMutation();

  const orderIdentifier = getOrderIdentifier(order);
  const isCancellable = canCancelOrder(order);
  const isRefundable = canRefundOrder(order);
  const isReleasable = canReleaseFromHoldingBin(order);
  const isUpdatable = canRequestPrinterUpdate(order);

  const handleCancelOrder = React.useCallback(() => {
    if (!isCancellable || !orderIdentifier) {
      return showNotification({ title: "Cancel order is disabled.", message: order.customerOrderId, color: "red" });
    }

    return openConfirmationModal(
      modals,
      order,
      {
        title: "Cancel order",
        confirmMessage: (
          <>
            Are you sure you want to cancel order <strong>{order.customerOrderId}</strong>?
          </>
        ),
        onConfirm: () => cancelOrderMutate({ orderIdentifier }),
        successMessage: "Order cancellation attempted.",
        errorMessage: "Failed to cancel order",
      },
      onRefetch
    );
  }, [isCancellable, order, orderIdentifier, modals, cancelOrderMutate, onRefetch]);

  const handleRefundOrder = React.useCallback(() => {
    if (!isRefundable) {
      return showNotification({ title: "Refund order is disabled.", message: order.customerOrderId, color: "red" });
    }
    return setOpened(true);
  }, [isRefundable, order]);

  const handleReprintOrder = React.useCallback(() => {
    if (!order.canReprint) {
      return showNotification({ title: "Reprint order is disabled.", message: order.customerOrderId, color: "red" });
    }

    return modals.openModal({
      title: "Reprint form",
      size: "lg",
      children: <ReprintForm order={order as GraphQL.OrderResponse} />,
    });
  }, [order, modals]);

  const handleReleaseFromHoldingBin = React.useCallback(() => {
    if (!isReleasable || !orderIdentifier) {
      return showNotification({
        title: "Release from holding bin is disabled.",
        message: order.customerOrderId,
        color: "red",
      });
    }

    return openConfirmationModal(
      modals,
      order,
      {
        title: "Release from holding bin",
        confirmMessage: (
          <>
            Are you sure you want to release order <strong>{order.customerOrderId}</strong> from holding bin?
          </>
        ),
        onConfirm: () => releaseFromHoldingBinMutate({ orderIdentifier }),
        successMessage: "Your order is scheduled for holding bin release.",
        errorMessage: "Failed to release order from holding bin.",
      },
      onRefetch
    );
  }, [isReleasable, order, orderIdentifier, modals, releaseFromHoldingBinMutate, onRefetch]);

  const handleUpdateOrder = React.useCallback(() => {
    if (!isUpdatable || !orderIdentifier) {
      return showNotification({
        title: "Request update from printer is disabled.",
        message: order.customerOrderId,
        color: "red",
      });
    }

    return openConfirmationModal(
      modals,
      order,
      {
        title: "Request update from printer",
        confirmMessage: (
          <>
            Are you sure you want to request update from printer for order <strong>{order.customerOrderId}</strong>?
          </>
        ),
        onConfirm: () => updateOrderStatus({ orderIdentifier }),
        successMessage: "Requested update from printer.",
        errorMessage: "Failed to request update from printer.",
      },
      onRefetch
    );
  }, [isUpdatable, order, orderIdentifier, modals, updateOrderStatus, onRefetch]);

  return (
    <Flex.Row gap={16} sx={{ flexWrap: "wrap" }}>
      <RpiButton
        variant="spark"
        size="small"
        width="fit"
        isLoading={cancelOrderMutation.isLoading}
        disabled={!isCancellable || !orderIdentifier}
        onClick={handleCancelOrder}
        children="Cancel Order"
      />

      {user.is.SUPER_ADMIN && (
        <>
          {!order.isSandbox && (
            <>
              <RpiButton
                variant="spark"
                size="small"
                width="fit"
                disabled={!isRefundable}
                onClick={handleRefundOrder}
                children="Refund Order"
              />

              <RpiButton
                variant="spark"
                size="small"
                width="fit"
                isLoading={cancelOrderMutation.isLoading}
                disabled={!order.canReprint}
                onClick={handleReprintOrder}
                children="Reprint"
              />
            </>
          )}

          <RpiButton
            variant="spark"
            size="small"
            width="fit"
            isLoading={releaseFromHoldingBinMutation.isLoading}
            disabled={!isReleasable || !orderIdentifier}
            onClick={handleReleaseFromHoldingBin}
            children="Release from Holding Bin"
          />

          <RpiButton
            variant="spark"
            size="small"
            width="fit"
            isLoading={updateOrderStatusMutation.isLoading}
            disabled={!isUpdatable || !orderIdentifier}
            onClick={handleUpdateOrder}
            children="Request Update from Printer"
          />
        </>
      )}

      <OrderRefundView opened={opened} onClose={() => setOpened(false)} />
    </Flex.Row>
  );
};
